import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Icon, Popup } from "semantic-ui-react";

import {
  AppointmentStatusCell,
  Pin as PinComponent,
  PinModal,
  QuestionStatusIcon,
  ReactTable,
  TableDropdownSelector,
  TableQuestionStatusButton,
  useCan
} from "components";
import { usePermanentLocations, usePermanentRealtimeDealers, useSelectedDealer, useSelectedLocation } from "components/DealersAndLocationsDropdown/hooks";
import { useRegisterNavAction } from "hooks";
import { ACL_ACTIVITIES, ACL_RESOURCES, PIN_STATUSES, QUESTION_RESULT_STATUSES, WARRANTY_TYPES } from "models";
import { useAppointmentLocation, useCarBrandsQuery } from "modules/AppointmentDetails/hooks";
import { getFormattedDate, getLocationName } from "modules/LeadsDashboard/util";
import { DealerLocationCard, OrderStatus } from "modules/WarrantyDashboard/components/WarrantyDashboardTable/components";
import { useGetSelectedFilterValues, useWarrantyDashboardTableFilterHandlers } from "modules/WarrantyDashboard/components/WarrantyDashboardTable/hooks";
import "modules/WarrantyDashboard/components/WarrantyDashboardTable/WarrantyDashboardTable.scss";
import {
  REG_VIN_FILTER,
  useAddPinMutation,
  useDeletePinMutation,
  useGetPinStatusOptions,
  useGetPinTypeOptions,
  useGetWarrantyDashboardFilters,
  useRegVinNumberOptions,
  useWarrantyPinModal,
  useWarrantyQuery
} from "modules/WarrantyDashboard/hooks";
import { PATHS } from "router/paths";

import type { Pin } from "models";
import type { DeletePinPayload } from "modules/AppointmentDetails/components/Interventions/hooks";

const columnHelper = createColumnHelper<Pin>();

//TODO: Need to revisit this and think about breaking down the ItemWithHistory<Pin> type to contain only what the table needs, it would speed up ts check,
// that would mean some of the functions/component would need to be updated not to require the whole types to be passed in, but just the part of that it needs,
// (e.g. Pin component requires the whole intervention model, but it only uses the: is_signed_by_customer and pin_history properties)
export const WarrantyDashboardTable = () => {
  const { t } = useTranslation();

  const { selectedDealer } = useSelectedDealer();
  const pinTypeOptions = useGetPinTypeOptions();
  const pinStatusOptions = useGetPinStatusOptions();
  const regVinNumberOptions = useRegVinNumberOptions();
  const { dealers } = usePermanentRealtimeDealers();
  const { selectedLocation } = useSelectedLocation();
  const { locations } = usePermanentLocations();
  const { data: brands = [] } = useCarBrandsQuery();
  const {
    filters: { page: currentPage, page_size: pageSize, dealer_ids, location_ids }
  } = useGetWarrantyDashboardFilters();
  const canCreateFullPin = useCan(ACL_ACTIVITIES.Create, ACL_RESOURCES.PinFull);

  const { mutateAsync: deletePinMutation, isPending: isDeletingPin } = useDeletePinMutation();
  const { mutateAsync: addPinMutation, isPending: isAddingPin } = useAddPinMutation();

  const onDeletePin = async (payload: DeletePinPayload) => {
    await deletePinMutation(payload);
  };

  const onAddPin = async (payload: Pin) => {
    if (!canCreateFullPin) return;
    await addPinMutation(payload);
  };

  const { pinWithHistory, handleOpenPinModal, handleClosePinModal } = useWarrantyPinModal();
  const { warrantyPins, nbItems, nbPages, isFetching, refetch: refetchWarranties } = useWarrantyQuery();
  const {
    setRegVinNumberFilter,
    selectedStatus,
    claimOrderArrow,
    selectedPinTypes,
    regVinNumberFilter,
    warrantyTypeOptions,
    selectedWarrantyType,
    questionStatusOptions,
    isDateRangeSelected,
    selectedAppointmentOrder,
    selectedAppointmentOrderText,
    appointmentOrderingOptions,
    isRecallOrRemarksSelected,
    locationOrderingOptions,
    selectedLocationOrder
  } = useGetSelectedFilterValues();
  const {
    handleClaimChange,
    handlePinTypeChange,
    handleQuestionChange,
    handlePageSizeChange,
    handleCopyToClipboard,
    handlePaginationChange,
    handleAppointmentChange,
    handleRegVinNumberChange,
    handleWarrantyTypeChange,
    handleLocationSortChange
  } = useWarrantyDashboardTableFilterHandlers({ setRegVinNumberFilter });

  useRegisterNavAction(() => (dealer_ids.length > 0 || location_ids.length > 0) && refetchWarranties());

  const columns = [
    columnHelper.accessor(row => row, {
      id: "appointment",
      header: () => (
        <div className="appointment-table-wrapper">
          <TableDropdownSelector
            direction="right"
            trigger={<span className="ellipsis ellipsis-wo-nr WarrantyDashboardTable__table-header-text">{selectedAppointmentOrderText}</span>}
            options={appointmentOrderingOptions}
            selected={[selectedAppointmentOrder]}
            handleOnSelect={handleAppointmentChange}
          />
        </div>
      ),
      size: 30,
      cell: ({
        row: {
          original: { appointment }
        }
      }) => <span> {getFormattedDate(appointment?.time_car_app)} </span>
    }),
    columnHelper.accessor(row => row, {
      id: "appointment_status",
      header: () => <span className="no-wrap-text WarrantyDashboardTable__table-header-text"> {t("v8_status", "Status")} </span>,
      size: 30,
      cell: ({
        row: {
          original: { appointment }
        }
      }) => {
        if (!appointment) return null;

        return (
          <AppointmentStatusCell
            shadow={false}
            value={{
              id: appointment.appointment_status_identifier,
              customcom_status: appointment.customer_communication?.status,
              car_check_paused_at: appointment.car_check_paused_at,
              car_out_of_shop: appointment.car_out_of_shop,
              car_in_shop: appointment.car_in_shop
            }}
            statuses={selectedLocation?.statuses}
          />
        );
      }
    }),

    columnHelper.accessor(row => row, {
      id: "wo",
      header: () => <span className="no-wrap-text WarrantyDashboardTable__table-header-text">WO # {nbItems}</span>,
      size: 30,
      cell: ({
        row: {
          original: { appointment }
        }
      }) => {
        if (!appointment) return "";

        return (
          <Popup
            hoverable
            content={appointment.wo_nr}
            trigger={
              <p className="ellipsis">
                <Link to={`${PATHS.APPOINTMENTS}/${appointment.id}`}>
                  <Icon className="arrow up right from square" />
                  {appointment.wo_nr}
                </Link>
              </p>
            }
          />
        );
      }
    }),

    columnHelper.accessor(row => row, {
      id: "reg_number",
      header: () => (
        <div className="appointment-table-wrapper">
          <TableDropdownSelector
            trigger={
              <span className="ellipsis ellipsis-wo-nr WarrantyDashboardTable__table-header-text">
                {regVinNumberFilter === REG_VIN_FILTER.Reg ? t("v8_reg_number", "Reg #") : t("v8_vin_number", "Vin #")}
              </span>
            }
            options={regVinNumberOptions}
            selected={[String(regVinNumberFilter)]}
            handleOnSelect={handleRegVinNumberChange}
          />
        </div>
      ),
      size: 30,
      cell: ({
        row: {
          original: { appointment }
        }
      }) => {
        if (!appointment) return "";
        const { car_id, is_lease_company, reg_number, vin_nr } = appointment;

        if (regVinNumberFilter === REG_VIN_FILTER.Vin) {
          return (
            vin_nr && (
              <div
                className="flex WarrantyDashboardTable__copy-to-clipboard"
                onClick={() => handleCopyToClipboard(vin_nr.toString(), t("v8_vin_nr_copied", "Vin # copied to clipboard"))}
              >
                <div className="WarrantyDashboardTable__ref-container">
                  <Icon name="copy outline" className="WarrantyDashboardTable__copy-icon" />
                  <Popup hoverable content={vin_nr} trigger={<p className="WarrantyDashboardTable__table-ellipsis">{vin_nr}</p>} />
                </div>
              </div>
            )
          );
        }

        return (
          <Popup
            hoverable
            content={reg_number}
            trigger={
              <p className="ellipsis">
                <Link to={`${PATHS.CAR_DETAIL}/${car_id}`}>
                  <Icon className="arrow up right from square" />
                  {reg_number}
                  {is_lease_company && <Icon name="building" />}
                </Link>
              </p>
            }
          />
        );
      }
    }),

    columnHelper.accessor(row => row, {
      id: "location",
      header: () => (
        <div className="appointment-table-wrapper">
          <TableDropdownSelector
            trigger={<span className="ellipsis ellipsis-wo-nr table-header-text">{t("v8_location", "Location")}</span>}
            options={locationOrderingOptions}
            selected={[selectedLocationOrder]}
            handleOnSelect={handleLocationSortChange}
          />
        </div>
      ),
      size: 40,
      cell: ({
        row: {
          original: { dealer_location_id }
        }
      }) => {
        if (!dealer_location_id) return null;

        return (
          <Popup
            hoverable
            content={<DealerLocationCard dealerLocationId={dealer_location_id} locations={locations} dealers={dealers} />}
            trigger={<p className="WarrantyDashboardTable__table-ellipsis">{getLocationName(dealer_location_id, locations)}</p>}
          />
        );
      }
    }),

    columnHelper.accessor(row => row, {
      id: "pin_type",
      header: () => (
        <div className="appointment-table-wrapper">
          <TableDropdownSelector
            trigger={<span className="ellipsis ellipsis-wo-nr table-header-text"> {t("v8_pin_type", "Pin Type")}</span>}
            multiple={true}
            options={pinTypeOptions}
            selected={selectedPinTypes}
            handleOnSelect={handlePinTypeChange}
          />
        </div>
      ),
      size: 50,
      cell: ({
        row: {
          original: { pin_type_id }
        }
      }) => {
        const content = pinTypeOptions.find(option => option.value === pin_type_id)?.text ?? "";

        return (
          <div className="WarrantyDashboardTable__warranty-pin-type-container">
            <Popup hoverable content={content} trigger={<p className="WarrantyDashboardTable__table-ellipsis">{content}</p>} />
          </div>
        );
      }
    }),

    columnHelper.accessor(row => row, {
      id: "warranty_type",
      header: () => (
        <div className="appointment-table-wrapper">
          <TableDropdownSelector
            disabled={isRecallOrRemarksSelected}
            options={warrantyTypeOptions}
            trigger={<span className="ellipsis ellipsis-wo-nr WarrantyDashboardTable__table-header-text"> {t("v8_warranty_type", "Warranty Type")}</span>}
            selected={[selectedWarrantyType]}
            handleOnSelect={handleWarrantyTypeChange}
          />
        </div>
      ),
      size: 30,
      cell: ({
        row: {
          original: { warranty_type_id }
        }
      }) => {
        const content = warranty_type_id !== WARRANTY_TYPES.NotSet ? (warrantyTypeOptions.find(option => option.value === warranty_type_id)?.text ?? "") : "";

        return (
          <div className="WarrantyDashboardTable__pin-status-container">
            <Popup hoverable content={content} trigger={<p className="WarrantyDashboardTable__table-ellipsis">{content}</p>} />
          </div>
        );
      }
    }),

    columnHelper.accessor(row => row, {
      id: "pin_status",
      header: () => <span className="no-wrap-text WarrantyDashboardTable__table-header-text"> {t("v8_pin_status", "Pin Status")}</span>,
      size: 30,
      cell: ({
        row: {
          original: { pin_status_id }
        }
      }) => {
        const content = pin_status_id !== PIN_STATUSES.NotSet ? (pinStatusOptions.find(option => option.value === pin_status_id)?.text ?? "") : "";

        return (
          <div className="WarrantyDashboardTable__pin-status-container">
            <Popup hoverable content={content} trigger={<p className="WarrantyDashboardTable__table-ellipsis">{content}</p>} />
          </div>
        );
      }
    }),

    columnHelper.accessor(row => row, {
      id: "support",
      header: () => <span className="no-wrap-text WarrantyDashboardTable__table-header-text">{t("v8_support", "Support")} #</span>,
      size: 30,
      cell: ({
        row: {
          original: { support_nr }
        }
      }) => {
        const content = support_nr.toString() || "";

        return (
          support_nr && (
            <div
              className="flex WarrantyDashboardTable__copy-to-clipboard"
              onClick={() => handleCopyToClipboard(support_nr.toString(), t("v8_support_nr_copied", "Support # copied to clipboard"))}
            >
              <div className="WarrantyDashboardTable__ref-container">
                <Icon name="copy outline" className="WarrantyDashboardTable__copy-icon" />
                <Popup hoverable content={content} trigger={<p className="WarrantyDashboardTable__table-ellipsis">{content}</p>} />
              </div>
            </div>
          )
        );
      }
    }),

    columnHelper.accessor(row => row, {
      id: "claim",
      header: () => (
        <div className="appointment-table-wrapper">
          <span className="no-wrap-text WarrantyDashboardTable__table-header-text">{t("v8_claim", "Claim")} #</span>
          <div>
            <Icon
              className={`caret ${claimOrderArrow} -cursor-pointer ${claimOrderArrow !== "down" && "WarrantyDashboardTable__claim-icon-filter"}`}
              onClick={handleClaimChange}
            />
          </div>
        </div>
      ),
      size: 30,
      cell: ({
        row: {
          original: { claim_nr }
        }
      }) => {
        const content = claim_nr ? claim_nr.toString() : "";

        return (
          claim_nr && (
            <div
              className="flex WarrantyDashboardTable__copy-to-clipboard"
              onClick={() => handleCopyToClipboard(content, t("v8_claim_nr_copied", "Claim # copied to clipboard"))}
            >
              <div className="WarrantyDashboardTable__ref-container">
                <Icon name="copy outline" className="WarrantyDashboardTable__copy-icon" />
                <Popup hoverable content={content} trigger={<p className="WarrantyDashboardTable__table-ellipsis">{content}</p>} />
              </div>
            </div>
          )
        );
      }
    }),

    columnHelper.accessor(row => row, {
      id: "ref",
      header: () => <span className="no-wrap-text WarrantyDashboardTable__table-header-text">{t("v8_ref", "Ref")} #</span>,
      size: 30,
      cell: ({
        row: {
          original: { ref_nr }
        }
      }) =>
        ref_nr ? (
          <div
            className="flex WarrantyDashboardTable__copy-to-clipboard"
            onClick={() => handleCopyToClipboard(ref_nr.toString(), t("v8_ref_nr_copied", "Ref # copied to clipboard"))}
          >
            <div className="WarrantyDashboardTable__ref-container">
              <Icon name="copy outline" className="WarrantyDashboardTable__copy-icon" />
              <Popup hoverable content={ref_nr} trigger={<p className="WarrantyDashboardTable__table-ellipsis">{ref_nr}</p>} />
            </div>
          </div>
        ) : null
    }),

    columnHelper.accessor(row => row, {
      id: "brand",
      header: () => <span className="no-wrap-text WarrantyDashboardTable__table-header-text">{t("v8_brand", "Brand")}</span>,
      size: 40,
      cell: ({
        row: {
          original: { appointment }
        }
      }) => {
        const content = !appointment ? "" : appointment.car_make;

        return (
          <div className="WarrantyDashboardTable__ref-container">
            <Popup hoverable content={content} trigger={<p className="WarrantyDashboardTable__table-ellipsis">{content}</p>} />
          </div>
        );
      }
    }),

    columnHelper.accessor(row => row, {
      id: "intervention_question_result",
      header: () => (
        <span className="no-wrap-text WarrantyDashboardTable__table-header-text">{t("v8_intervention_question_result", "Intervention/Question Result")}</span>
      ),
      size: 100,
      cell: ({
        row: {
          original: { intervention, question_result }
        }
      }) => {
        const content = question_result ? question_result.title : intervention?.title;

        return (
          <div className="WarrantyDashboardTable__intervention-question-result-container">
            <Popup hoverable content={content} trigger={<p className="WarrantyDashboardTable__table-ellipsis">{content}</p>} />
          </div>
        );
      }
    }),

    columnHelper.accessor(row => row, {
      id: "actions",
      header: () => (
        <div className="WarrantyDashboardTable__table-action-buttons-container">
          <TableDropdownSelector
            selected={[selectedStatus]}
            options={questionStatusOptions}
            handleOnSelect={handleQuestionChange}
            trigger={<TableQuestionStatusButton selectedQuestionStatuses={[selectedStatus]} />}
            scrolling={false}
            showFilterIcon={false}
          />
          <div className="WarrantyDashboardTable__customer-approved-table-column">
            <Icon className="face smile" />
          </div>
          <div>
            <Icon className="car" />
          </div>
          <div className="WarrantyDashboardTable__table-icon-placeholder-div WarrantyDashboardTable__table-icon-placeholder-div-margin" />
          <div className="WarrantyDashboardTable__table-icon-placeholder-div" />
        </div>
      ),
      size: 40,
      cell: ({ row: { original } }) => {
        const { question_result_id, question_result, intervention, appointment, order_status, mechanic_fixed } = original;
        const customerOk = question_result_id ? question_result?.customer_approved : intervention?.customer_ok;
        const questionResultStatus = question_result?.status ?? intervention?.question_result_status ?? null;
        const location = useAppointmentLocation(appointment);

        return (
          <div className="WarrantyDashboardTable__table-action-buttons-container">
            {questionResultStatus !== QUESTION_RESULT_STATUSES.NoStatus ? (
              <div className="WarrantyDashboardTable__table-action-buttons-container">
                <QuestionStatusIcon status={questionResultStatus} />
              </div>
            ) : (
              <div className="WarrantyDashboardTable__table-icon-placeholder-div" />
            )}
            <div>
              <Icon className={`circle ${customerOk ? "check i-green" : "remove red"}`} />
            </div>
            {mechanic_fixed ? (
              <div className="WarrantyDashboardTable__warranty-mechanic-fixed-status-container">
                <Icon className="wrench i-green" />
                <Icon className="check circle i-green fixed-icon-container" />
              </div>
            ) : (
              <div className="WarrantyDashboardTable__mechanic-not-fixed ">
                <Icon className="wrench regular grey rotate-270 " />
              </div>
            )}
            {order_status ? <OrderStatus orderStatus={order_status} /> : <div className="WarrantyDashboardTable__table-icon-placeholder-div" />}
            {appointment && location && (
              <PinComponent
                pin={{ ...original, history: [] }}
                vin={appointment.vin_nr}
                location={location}
                brands={brands}
                intervention={intervention}
                questionResult={question_result}
                onClick={() => handleOpenPinModal(original)}
              />
            )}
          </div>
        );
      }
    })
  ];

  return (
    <>
      <div className="WarrantyDashboardTable">
        <ReactTable
          isFetching={isFetching}
          columns={columns}
          data={warrantyPins}
          pageCount={nbPages}
          pageSize={pageSize}
          totalItems={nbItems}
          pagination={nbPages > 1 ? { pageIndex: currentPage - 1, pageSize } : undefined}
          onChangePageSize={handlePageSizeChange}
          onPaginationChange={handlePaginationChange}
          overrideTableBodyMessage={
            !isDateRangeSelected ? `${t("v8_please_select_at_least_one_date_range_to_filter_on", "Please select at least one date range to filter on")}.` : null
          }
        />
      </div>
      {pinWithHistory && (
        <PinModal
          isOpen={!!pinWithHistory}
          dealer={selectedDealer}
          onClose={handleClosePinModal}
          pin={pinWithHistory}
          onDeletePin={onDeletePin}
          onSavePin={onAddPin}
          isDeletingPin={isDeletingPin}
          isSavingPin={isAddingPin}
        />
      )}
    </>
  );
};
